import { UploadedTemplate, TemplateSection } from 'src/data-capture/hooks/templates/useTemplates';
import {
  TemplateExportData,
  ExportedTemplateSection,
  SectionSplittingLevel,
  TemplateType,
  TableOfContentsDepth,
  DEFAULT_TABLE_OF_CONTENTS_DEPTH,
  DEFAULT_TABLE_OF_CONTENTS_TITLE,
} from 'src/company/templates/types';

export const clampTableOfContentsDepth = (depth: unknown): TableOfContentsDepth => {
  const parsed = Number(depth);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TABLE_OF_CONTENTS_DEPTH;
  }

  return Math.min(4, Math.max(1, Math.round(parsed))) as TableOfContentsDepth;
};

const extractHeaderFromMarkdown = (markdown: string) => {
  const match = markdown.match(/^(#{1,6})\s+(.+)$/m);
  if (!match) {
    return null;
  }

  return {
    text: match[2].replace(/\s+#+\s*$/, '').trim(),
    level: match[1].length,
  };
};

function exportTemplateToJson(template: UploadedTemplate): TemplateExportData {
  return {
    version: 1,
    templateType: template.templateType,
    templateName: template.displayName,
    templateDescription: template.description,
    markdownSplitLevel: 2 as SectionSplittingLevel,
    sectionSplittingLevel: (template.sectionSplittingLevel ?? null) as SectionSplittingLevel | null,
    sections: template.sections.map(
      (section: TemplateSection): ExportedTemplateSection => ({
        sectionHeader: section.sectionHeader,
        sectionHeaderLevel: section.sectionHeaderLevel,
        templatedMarkdown: section.templateText,
        populatedMarkdown: section.templateText,
        sectionInstructions: section.sectionInstructions,
        preserveAsIs: section.preserveAsIs,
        agent: section.agent,
        domainFilter: section.domainFilter,
        addPageBreakAfterSection: section.addPageBreakAfterSection,
        sectionType: section.sectionType,
        tableOfContentsDepth: section.tableOfContentsDepth,
        addCoverPageBeforeSection: section.addCoverPageBeforeSection,
        inclusionCondition: section.inclusionCondition,
      }),
    ),
    meetingNoteTense: template.meetingNoteTense,
    eligibleForSummarisation: template.eligibleForSummarisation,
  };
}

export function downloadTemplateJson(template: UploadedTemplate): void {
  const exportData = exportTemplateToJson(template);
  const jsonString = JSON.stringify(exportData, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `${template.displayName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_template.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function parseTemplateJson(file: File): Promise<TemplateExportData> {
  const text = await file.text();
  const data = JSON.parse(text);

  if (!data.version || data.version !== 1) {
    throw new Error('Invalid template file version');
  }
  if (!data.templateType || !data.templateName || !data.sections) {
    throw new Error('Invalid template file format');
  }
  if (!Array.isArray(data.sections) || data.sections.length === 0) {
    throw new Error('Template must have at least one section');
  }

  const validTypes: TemplateType[] = [
    'MEETING_NOTE',
    'MEETING_AGENDA',
    'SUITABILITY_REPORT',
    'CLIENT_INFORMATION_DOCUMENT',
  ];
  if (!validTypes.includes(data.templateType)) {
    throw new Error(`Invalid template type: ${data.templateType}`);
  }

  const tableOfContentsSections = data.sections.filter(
    (section: ExportedTemplateSection) => section.sectionType === 'TABLE_OF_CONTENTS',
  );
  if (tableOfContentsSections.length > 1) {
    throw new Error('A template can have at most one table of contents section');
  }

  const normalizedSections = data.sections.map((section: ExportedTemplateSection) => {
    const isTableOfContents = section.sectionType === 'TABLE_OF_CONTENTS';
    // A contents section has no prose of its own, so it is the one section allowed to carry no markdown.
    const templatedMarkdown =
      section.templatedMarkdown ?? section.populatedMarkdown ?? (isTableOfContents ? '' : null);

    if (templatedMarkdown == null) {
      throw new Error('Each section must have templatedMarkdown or populatedMarkdown');
    }

    if (isTableOfContents) {
      return {
        ...section,
        sectionType: 'TABLE_OF_CONTENTS' as const,
        sectionHeader:
          (typeof section.sectionHeader === 'string' ? section.sectionHeader.trim() : '') ||
          DEFAULT_TABLE_OF_CONTENTS_TITLE,
        sectionHeaderLevel: Number(section.sectionHeaderLevel) || 2,
        templatedMarkdown: '',
        populatedMarkdown: '',
        preserveAsIs: false,
        tableOfContentsDepth: clampTableOfContentsDepth(section.tableOfContentsDepth),
        addCoverPageBeforeSection: section.addCoverPageBeforeSection ?? false,
        inclusionCondition: null,
      };
    }

    const inferredHeader = extractHeaderFromMarkdown(templatedMarkdown);
    const rawHeader = typeof section.sectionHeader === 'string' ? section.sectionHeader.trim() : '';
    const sectionHeader = rawHeader || inferredHeader?.text || '';
    const rawHeaderLevel = Number(section.sectionHeaderLevel);
    const sectionHeaderLevel =
      Number.isFinite(rawHeaderLevel) && rawHeaderLevel >= 1 && rawHeaderLevel <= 6
        ? rawHeaderLevel
        : (inferredHeader?.level ?? 2);

    return {
      ...section,
      sectionType: 'CONTENT' as const,
      sectionHeader,
      sectionHeaderLevel,
      templatedMarkdown,
      populatedMarkdown: section.populatedMarkdown ?? templatedMarkdown,
    };
  });

  return {
    ...data,
    sections: normalizedSections,
  } as TemplateExportData;
}
